import { Component, ElementRef, Input, OnInit, Optional, Renderer2, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { I18nCountryService } from './i18n-country.service';
import { II18nCountry } from './i-i18n-country';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import sortBy from 'lodash/sortBy';
import findIndex from 'lodash/findIndex';
import pullAt from 'lodash/pullAt';
import * as Raven from 'raven-js';

@Component({
  standalone: false,
  selector: 'ui-i18n-phone-input',
  templateUrl: './ui-i18n-phone-input.component.html',
  styleUrls: ['./ui-i18n-phone-input.component.scss'],
  providers: [I18nCountryService],
})
export class UiI18nPhoneInputComponent implements OnInit, ControlValueAccessor {
  @Input() preferredCountries: string[] = ['SE', 'NO', 'FI', 'DK', 'LV', 'GB', 'PL', 'DE', 'EE', 'BE'];
  @Input() defaultCountryCode: string;
  @Input() takeLimit = 5;

  @Input() public placeholder: string;
  @Input() public name: string;
  @Input() public label: string;
  @Input() public labelFontSize = 'xl';
  @Input() public showLabel = true;

  @ViewChild('phoneNumberInput', { static: false }) phoneNumberInput: ElementRef<HTMLInputElement>;

  get value(): string {
    return this._value ? this.extractNumber(this._value) : this._value;
  }

  set value(value: string) {
    this._value = value;
  }

  @Input()
  get disabled(): boolean {
    if (this.control) {
      return this.control.disabled;
    }
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input()
  get required(): boolean {
    if (this.control) {
      return this.control.hasError('required');
    }
    return this._required;
  }

  set required(value: boolean) {
    this._required = value;
  }

  @Input()
  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = value;
  }

  get control(): AbstractControl {
    if (this._ngControl?.control) {
      return this._ngControl.control;
    } else {
      console.error('ui-i18n-phone-input not a ngControl');
      Raven.captureException('ui-i18n-phone-input not a ngControl');
    }
  }

  get allCountries(): II18nCountry[] {
    return this._countryService.allCountries;
  }

  get selectedCountry(): II18nCountry {
    if (this._selectedCountry) {
      return this._selectedCountry;
    }
    return this.allCountries[0];
  }

  set selectedCountry(country: II18nCountry) {
    this._selectedCountry = country;
    if (this.preferredCountriesInDropDown.length > 0) {
      this.preferredCountriesInDropDown = this.putSelectedCountryTopList(country);
    }
  }

  public onTouched: () => void;
  public isOpenCountryContainer = false;
  public nothingFoundCountry = null;
  public preferredCountriesInDropDown: II18nCountry[] = [];
  public isSendingNothingFoundRequest = false;

  private _onChange: (value: string) => void;
  private _value: string = null;
  private _selectedCountry: II18nCountry;
  private _disabled = false;
  private _required = false;
  private _readonly = false;
  private _defaultCountryCode = 'NO';

  constructor(
    private _renderer: Renderer2,
    protected _translateService: TranslateService,
    @Optional() @Self() private _ngControl: NgControl,
    private _countryService: I18nCountryService,
  ) {
    if (_ngControl) {
      _ngControl.valueAccessor = this;
    }

    if (this.preferredCountries.length) {
      this.preferredCountriesInDropDown = this._countryService.sortCountriesByPreferredIso2Codes(
        this.preferredCountries,
      );
    }

    this._setSelectedCountryByCountryCode(this._defaultCountryCode);
  }

  ngOnInit() {
    if (this._defaultCountryCode !== this.defaultCountryCode) {
      this._defaultCountryCode = this.defaultCountryCode;

      if (!this.value) {
        this._setSelectedCountryByCountryCode(this.defaultCountryCode);
      }
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // setDisabledState(isDisabled: boolean): void {
  //   this.disabled = isDisabled;
  //   this._renderer.setProperty(this.phoneNumberInput.nativeElement, 'disabled', isDisabled);
  // }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (!this.phoneNumberInput?.nativeElement) {
      return;
    }

    this._renderer.setProperty(this.phoneNumberInput.nativeElement, 'disabled', isDisabled);
  }

  writeValue(value: string): void {
    this.value = value;
    if (!value) {
      return;
    }

    let country: II18nCountry = null;
    if (value.indexOf('+') === 0) {
      const { code } = this._countryService.parseNumber(value);
      country = this._countryService.getCountryByDialCode(code);
    } else {
      country = this._countryService.getCountryByIso2Code(this._defaultCountryCode);
    }

    if (country) {
      this.selectedCountry = country;
    }

    const { localNumber, normalized } = this._countryService.normalizePhoneNumber(value, country);
    this.value = localNumber;

    setTimeout(() => this._onChange(normalized));
  }

  public onPhoneNumberChange(event: any): void {
    const { value } = event.target;

    const { localNumber, normalized, updatedCountryIsoCode } = this._countryService.normalizePhoneNumber(
      value,
      this.selectedCountry,
    );
    // when users pastes a number with different country code
    if (updatedCountryIsoCode) {
      this._setSelectedCountryByCountryCode(updatedCountryIsoCode);
    }

    this.value = localNumber;

    // If value was transformed, explicitly update the input element's DOM value
    // Angular's [ngModel] binding won't update it during the same input event cycle
    if (value !== localNumber && this.phoneNumberInput && this.phoneNumberInput.nativeElement) {
      this.phoneNumberInput.nativeElement.value = localNumber;
    }

    this._onChange(normalized);
  }

  public searchCountry(query: string): Observable<II18nCountry[]> {
    if (!query) {
      return of(this.preferredCountriesInDropDown || []);
    }

    query = query.trim();
    const loweredSearchQuery = this.removePlusFromBeginningLine(query).toString().toLowerCase();

    const filteredCountries = this.preferredCountriesInDropDown
      .filter((country) => {
        const regExpr = new RegExp(loweredSearchQuery, 'm');
        const keyExpression: string =
          `${country.name} ${country.dialCode} ${this._translateService.instant(country.translate)}`.toLowerCase();
        return regExpr.test(keyExpression);
      })
      .map((c) => {
        return {
          ...c,
          priority: c.name.toLowerCase().indexOf(loweredSearchQuery),
        };
      });
    const sortedCountries = sortBy(filteredCountries, 'priority').sort((country: II18nCountry) => {
      return country.dialCode.toString() === this.selectedCountry.dialCode.toString() ? 1 : -1;
    });

    if (sortedCountries.length > 0) {
      this.nothingFoundCountry = null;
      return of(sortedCountries);
    }

    this.nothingFoundCountry = {
      nothingFound: true,
      query,
    };
    return of([]);
  }

  public onSelectCountry(country: II18nCountry): void {
    this.selectedCountry = country;

    if (this.value) {
      const { normalized } = this._countryService.normalizePhoneNumber(this.value, country);

      this._onChange(normalized);
    }

    this.onCloseCountryContainer();
  }

  public onOpenCountryContainer(event: Event): void {
    if (this.disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    this.isOpenCountryContainer = true;
  }

  public onCloseCountryContainer(): void {
    if (this.phoneNumberInput && this.phoneNumberInput.nativeElement) {
      this.phoneNumberInput.nativeElement.focus();
    }

    this.isOpenCountryContainer = false;
  }

  public onClickOutside(event: any, toggler: any): void {
    if (event['value'] && !this._isTogglerClick(event['target'], toggler)) {
      this.onCloseCountryContainer();
    }
  }

  public sendNothingFoundExceptionToSentry(): void {
    Raven.captureException(`user could not find country by criteria ${this.nothingFoundCountry.query}`);

    this.isSendingNothingFoundRequest = true;
    setTimeout(() => {
      this.isSendingNothingFoundRequest = false;
      this.nothingFoundCountry = false;
    }, 1000);
  }

  private removePlusFromBeginningLine(str: string): string {
    if (str.indexOf('+') === 0) {
      return this.removePlusFromBeginningLine(str.slice(1));
    }
    return str;
  }

  private _isTogglerClick(node, toggler) {
    while (node !== null) {
      if (node.id === toggler || (node.classList && node.classList.contains(toggler))) {
        return true;
      } else {
        node = node.parentNode;
      }
    }
    return false;
  }

  private extractNumber(value: string): string {
    if (value && value.indexOf('+') === 0) {
      return this._countryService.parseNumber(value).localNumber;
    }

    return value;
  }

  private _setSelectedCountryByCountryCode(countryCode: string) {
    const country = this._countryService.getCountryByIso2Code(countryCode.toUpperCase());
    if (country) {
      this.selectedCountry = country;
    } else {
      Raven.captureException(`Default country not found by iso2 code - ${this._defaultCountryCode}`);
    }
  }

  private putSelectedCountryTopList(selectedCountry: II18nCountry): II18nCountry[] {
    const countries = this.preferredCountriesInDropDown;
    if (countries[0].iso2 !== selectedCountry.iso2) {
      const selectedCountryIndex = findIndex(countries, { iso2: selectedCountry.iso2 });
      pullAt(countries, selectedCountryIndex);
      countries.unshift(selectedCountry);
      return countries;
    }
    return countries;
  }
}
